import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  DataTableDataSearchDto,
  DetailPageWithoutFilterDto,
  InfoIconDto,
  TableColumnConfigDto,
  TableDataDto,
  TableDto,
  TableRowDto,
} from '@asset-metrix/portal-gateway-client-js';
import { WidgetProps } from '@asset-metrix/portal-ui';

import { NO_ROLE_ID } from '../../api/portalGatewayClient';
import { useStore } from '../../store';
import { Filter } from '../../store/states/filterTypes';

import { getDetailPageUrl } from './getDetailPageUrl';
import { useOriginHrefForDetailsPage } from './useOriginHrefForDetailsPage';
import { getId, widgetBulkAction } from './widgetBulkAction';
import { widgetShareAction } from './widgetShareAction';

export type BaseUseTableWidgetActionProps = {
  customFilterColumns: TableColumnConfigDto[];
  infoIcon?: InfoIconDto;
  isCustomizable: boolean;
  isColumnInitiallyHidden: boolean;
  isCustomized: boolean;
  onModalOpened?: (
    modalId: string,
    rowIds: string[],
    action: string,
    data: TableRowDto['data'][],
    documentId?: string
  ) => void;
  title: string;
  widgetId: string;
  closeFullscreen?: () => void;
  onBulkUpdateTableRowModalOpened?: (
    rowData: { rowId: string; data: TableRowDto['data'] }[]
  ) => void;
  onInitFundOperationModalOpened?: () => void;
  onCreateRowModalOpened?: () => void;
  openCustomFilter: () => void;
  openCustomization: () => void;
  openDocUpload: () => void;
  openFullscreen?: () => void;
  openInfoIcon: () => void;
  openZipUpload: () => void;
  openDeleteConfirmation: (rowIds: string[], onApply: () => void) => void;
};

export const useTableWidgetActions = ({
  activeCustomFilterIds,
  config,
  currentSelectedRows,
  customFilterColumns,
  infoIcon,
  isCustomizable,
  isColumnInitiallyHidden,
  isCustomized,
  pageFilter,
  pageFilterSelections,
  tableFilterSelections,
  workflowStateSelection,
  title,
  widgetId,
  closeFullscreen,
  onModalOpened,
  onBulkUpdateTableRowModalOpened,
  onInitFundOperationModalOpened,
  onCreateRowModalOpened,
  openCustomFilter,
  openCustomization,
  openDocUpload,
  openFullscreen,
  openInfoIcon,
  openZipUpload,
  openDeleteConfirmation,
}: BaseUseTableWidgetActionProps & {
  activeCustomFilterIds: string[];
  config: TableDto['config'];
  currentSelectedRows: TableDataDto['items'];
  pageFilter?: Filter;
  pageFilterSelections: DataTableDataSearchDto['selection'];
  tableFilterSelections: DataTableDataSearchDto['selection'];
  workflowStateSelection: DataTableDataSearchDto['workflowStateSelection'];
}) => {
  const { t } = useTranslation();
  const originHref = useOriginHrefForDetailsPage();
  const actions: WidgetProps['actions'] = [];
  const roleId = useStore((state) => state.activeUserRole?.id) || NO_ROLE_ID;
  const navigate = useNavigate();

  if (config.initFundOperationJsonSchema) {
    actions.push({
      icon: { name: 'add' },
      label: t('app.widget.action.label.initFoundOperation'),
      title: t('app.widget.action.label.initFoundOperation'),
      cb: async () => {
        onInitFundOperationModalOpened?.();
      },
    });
  }

  if (
    config.createRowConfig &&
    config.createRowConfig.type !== 'unrealizedInvestmentRow'
  ) {
    const { buttonLabel } = config.createRowConfig;
    actions.push({
      icon: { name: 'add' },
      label: buttonLabel ?? t('app.widget.action.label.createRow'),
      title: buttonLabel ?? t('app.widget.action.label.createRow'),
      cb: async () => {
        onCreateRowModalOpened?.();
      },
    });
  }

  if (config.enableDocumentZipUpload) {
    actions.push({
      icon: { svgIcon: true, name: 'upload-zip' },
      title: t('app.upload.uploadZip'),
      cb: openZipUpload,
    });
  }

  if (config.uploadJsonSchema) {
    actions.push({
      icon: { name: 'file-move', svgIcon: true },
      title: t('app.upload.uploadDocuments'),
      cb: openDocUpload,
    });
  }

  if (config.updateJsonSchema) {
    actions.push({
      disabled:
        currentSelectedRows.length === 0 ||
        (config.maxBulkEditItemCount != null &&
          currentSelectedRows.length > config.maxBulkEditItemCount),
      icon: { name: 'edit' },
      title: t('app.widget.action.label.edit'),
      cb: async () => {
        const rowData = currentSelectedRows.map((row) => ({
          rowId: String(getId(row)),
          data: row,
          file_name: row.file_name,
        }));

        onBulkUpdateTableRowModalOpened?.(rowData);
      },
    });
  }

  if (customFilterColumns.length > 0) {
    actions.push({
      icon:
        activeCustomFilterIds.length > 0
          ? { name: 'filter-active', svgIcon: true }
          : { name: 'filter', svgIcon: true },
      title: t('app.widget.action.label.customFilter'),
      cb: openCustomFilter,
    });
  }

  if (isCustomizable) {
    actions.push({
      icon: isCustomized
        ? { name: 'settings-active', svgIcon: true }
        : isColumnInitiallyHidden
          ? { name: 'settings-invisible', svgIcon: true }
          : { name: 'settings', solid: true },
      title: t('app.widget.action.label.customization'),
      cb: openCustomization,
    });
  }

  const widgetShareActions =
    config.shareActions?.map((sa) =>
      widgetShareAction(widgetId, title || config.title, sa, t, {
        pageFilterSelections,
        tableFilterSelections,
        workflowStateSelection,
      })
    ) ?? [];

  if (widgetShareActions.length > 0) {
    actions.push({
      icon: { name: 'share' },
      title: t('app.widget.action.label.shareActions'),
      moreActions: widgetShareActions,
    });
  }

  if (infoIcon != null) {
    actions.push({
      icon: { name: 'info' },
      title: t('app.widget.action.label.additionalInformation'),
      cb: openInfoIcon,
    });
  }

  if (openFullscreen) {
    actions.push({
      icon: { name: 'open_in_full' },
      title: t('app.widget.action.label.openInFullscreen'),
      cb: openFullscreen,
    });
  }
  if (closeFullscreen) {
    actions.push({
      icon: { name: 'close' },
      title: t('app.widget.action.label.closeFullscreen'),
      cb: closeFullscreen,
    });
  }

  const openDetailPage = (
    detailPageConfig: DetailPageWithoutFilterDto,
    dataRows: Record<string, unknown>[]
  ) => {
    const label = dataRows
      .map((row) => row[detailPageConfig.detailsIdKey])
      .join(', ');
    const href = getDetailPageUrl(
      roleId,
      { ...detailPageConfig, additionalFilterColumnKeys: [] },
      pageFilter,
      dataRows,
      label
    );

    navigate(href, {
      state: { originHref },
    });
  };

  const widgetBulkActions =
    config.bulkActions?.map((ba) =>
      widgetBulkAction(
        widgetId,
        title || config.title,
        ba,
        config.cacheKey,
        currentSelectedRows,
        pageFilterSelections,
        t,
        openDetailPage,
        openDeleteConfirmation,
        onModalOpened
      )
    ) ?? [];

  if (widgetBulkActions.length > 0) {
    actions.push({
      icon: { name: 'more_vert' },
      title: t('app.widget.action.label.bulkActions'),
      moreActions: widgetBulkActions,
    });
  }

  return actions;
};
